import React from 'react';
import { spacingMobile, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * MobileRangeSlider Component
 *
 * Touch-optimized range slider for mobile devices.
 * Features larger touch targets (44px), bigger thumb (28px), and thicker track (12px).
 */
const MobileRangeSlider = ({
  min,
  max,
  step,
  value,
  onChange,
  disabled = false,
  label,
  hint,
  ...props
}) => {
  const { colors } = useTheme();
  const progressFactor = (value - min) / (max - min);

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.9375rem',
          fontWeight: 500,
          marginBottom: spacingMobile.md,
          color: colors.foreground
        }}>
          {label}
        </label>
      )}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
        '--progress-track-factor': progressFactor,
        '--track-thickness': '12px',
        '--thumb-size': '28px',
      }}>
        {/* Background track */}
        <div style={{
          position: 'absolute',
          left: '0',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '100%',
          height: 'var(--track-thickness)',
          background: 'hsla(240, 1%, 44%, 0.4)',
          borderRadius: borderRadius.full,
          pointerEvents: 'none',
        }} />

        {/* Progress track */}
        <div style={{
          position: 'absolute',
          left: '0',
          top: '50%',
          transform: 'translateY(-50%)',
          width: `calc(100% * ${progressFactor})`,
          height: 'var(--track-thickness)',
          background: colors.foreground,
          borderRadius: borderRadius.full,
          pointerEvents: 'none',
          zIndex: 5,
        }} />

        {/* Range input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={onChange}
          style={{
            position: 'relative',
            width: '100%',
            height: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            appearance: 'none',
            WebkitAppearance: 'none',
            background: 'transparent',
            outline: 'none',
            opacity: disabled ? 0.6 : 1,
            zIndex: 10,
          }}
          {...props}
        />

        <style>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: var(--thumb-size);
            height: var(--thumb-size);
            border-radius: 50%;
            background: ${colors.foreground};
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.2, 1, 0.2, 1);
          }

          input[type="range"]:active::-webkit-slider-thumb {
            transform: scale(1.15);
            box-shadow: 0 0 0 4px hsla(0, 0%, 9%, 0.4);
          }

          input[type="range"]:focus-visible::-webkit-slider-thumb {
            box-shadow: 0 0 0 4px ${colors.foregroundSecondary};
          }

          input[type="range"]::-moz-range-thumb {
            width: var(--thumb-size);
            height: var(--thumb-size);
            border: none;
            border-radius: 50%;
            background: ${colors.foreground};
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.2, 1, 0.2, 1);
          }

          input[type="range"]:active::-moz-range-thumb {
            transform: scale(1.15);
            box-shadow: 0 0 0 4px hsla(0, 0%, 9%, 0.4);
          }

          input[type="range"]:focus-visible::-moz-range-thumb {
            box-shadow: 0 0 0 4px ${colors.foregroundSecondary};
          }
        `}</style>
      </div>
      {hint && (
        <div style={{
          fontSize: '0.8125rem',
          color: colors.foregroundSecondary,
          marginTop: spacingMobile.sm
        }}>
          {hint}
        </div>
      )}
    </div>
  );
};

export default MobileRangeSlider;
